import React, { useEffect } from "react";
import { Col, Form, Row, Modal, Button } from "react-bootstrap";
import BootCheckbox from "components/common/custom/forms/BootCheckbox";
import BootDatePicker from "components/common/custom/forms/BootDatePicker";
import MultiSelect from "components/common/MultiSelect";
import { Controller } from "react-hook-form";
import { ShowError } from "helpers/utils";
import { useDispatch, useSelector } from "react-redux";
import {
  clearValidationErrors,
  handleFillStepData,
} from "../config/claimSlice";
import { toast } from "react-toastify";

const OpticalDetailsEditModal = ({
  showModal,
  setShowModal,
  productType,
  isLens,
  isContact,
  control,
  watch,
  setValue,
  handleSubmit,
  renderEyeTable,
  onSubmit,
  errors,
  reset,
}) => {
  const dispatch = useDispatch();
  const { validationErrors } = useSelector((state) => state.claim);
  useEffect(() => {
    if (showModal) {
      reset();
      dispatch(clearValidationErrors());
    }
  }, [showModal]);

  const handleReset = () => {
    // Define all optical fields to reset (excluding productType and optical_date)
    const fieldsToReset = [
      // Regular lenses/contact fields
      "regularLenses",
      "regular_contact",
      "is_frame",
      "lens_specifications",

      // Left eye - distance
      "left_sph_distance",
      "left_cyl_distance",
      "left_axis_distance",
      "left_power_distance",
      "left_backcurve_distance",
      "left_diameter_distance",
      "left_in_distance",
      "left_out_distance",
      "left_up_distance",
      "left_down_distance",

      // Left eye - near
      "left_sph_near",
      "left_cyl_near",
      "left_axis_near",
      "left_power_near",
      "left_backcurve_near",
      "left_diameter_near",
      "left_in_near",
      "left_out_near",
      "left_up_near",
      "left_down_near",

      // Right eye - distance
      "right_sph_distance",
      "right_cyl_distance",
      "right_axis_distance",
      "right_power_distance",
      "right_backcurve_distance",
      "right_diameter_distance",
      "right_in_distance",
      "right_out_distance",
      "right_up_distance",
      "right_down_distance",

      // Right eye - near
      "right_sph_near",
      "right_cyl_near",
      "right_axis_near",
      "right_power_near",
      "right_backcurve_near",
      "right_diameter_near",
      "right_in_near",
      "right_out_near",
      "right_up_near",
      "right_down_near",

      // Common fields for both eyes
      "left_add",
      "left_color",
      "left_brand",
      "left_duration",
      "left_duration_unit",
      "right_add",
      "right_color",
      "right_brand",
      "right_duration",
      "right_duration_unit",
    ];

    // Reset all fields to empty/default values
    fieldsToReset.forEach((field) => {
      if (field === "is_frame") {
        setValue(field, false);
      } else if (field === "lens_specifications") {
        setValue(field, []);
      } else {
        setValue(field, "");
      }
    });

    // Clear from Redux as well
    fieldsToReset.forEach((field) => {
      let value = "";
      if (field === "is_frame") value = false;
      if (field === "lens_specifications") value = [];
      // dispatch(handleFillStepData({ check: field, value }));
    });
  };


  return (
    <Modal
      show={showModal}
      // onHide={() => setShowModal(false)}
      size="xl"
      centered
    >
      <Modal.Header className="fs-10 text-white">
        <Modal.Title className="fs-10">Edit Optical Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row className="g-2 mb-3">
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label className="fs-10 fw-semibold">
                  Product Type
                </Form.Label>
                <Controller
                  name="productType"
                  control={control}
                  render={({ field }) => (
                    <Form.Select
                      {...field}
                      className="fs-10"
                      size="sm"
                      onChange={(e) => {
                        field.onChange(e); // standard rhf onChange
                        setValue("productType", e.target.value);
                        handleReset(); // reset other fields
                      }}
                    >
                      <option value="">Select Product Type</option>
                      <option value="lens">Lens</option>
                      <option value="contact">Contact</option>
                    </Form.Select>
                  )}
                />
              </Form.Group>
            </Col>
            {productType && (
              <>
                <Col xs={12} md={4}>
                  <BootDatePicker
                    name="optical_date"
                    label="Prescription Date"
                    size="sm"
                    control={control}
                    rules={{ required: productType }}
                    errors={errors}
                    selected={watch("optical_date")}
                    onChange={(newValue) => setValue("optical_date", newValue)}
                    placeholder="dd/mm/yyyy"
                    isRequired={productType}
                    className="fs-10"
                  />
                </Col>
                <Col xs={12} md={4}>
                  {isLens && (
                    <Row className="g-2">
                      <Col xs={9}>
                        <Form.Group>
                          <Form.Label className="fs-10 fw-semibold">
                            Regular Lenses
                          </Form.Label>
                          <Controller
                            name="regularLenses"
                            control={control}
                            render={({ field }) => (
                              <Form.Select
                                {...field}
                                className="fs-10"
                                size="sm"
                                onChange={(e) => {
                                  field.onChange(e); // standard rhf onChange
                                  setValue("regularLenses", e.target.value);
                                  handleReset(); // reset other fields
                                }}
                              >
                                <option value="">Select Lens Type</option>
                                <option value="glass">Glass</option>
                                <option value="plastic">Plastic</option>
                                <option value="other">Other</option>
                              </Form.Select>
                            )}
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={3}>
                        <BootCheckbox
                          name="is_frame"
                          label="Is Frame?"
                          control={control}
                          size="lg"
                          className="fs-10"
                        />
                      </Col>
                    </Row>
                  )}
                  {isContact && (
                    <Form.Group>
                      <Form.Label className="fs-10 fw-semibold">
                        Regular Contact
                      </Form.Label>
                      <Controller
                        name="regular_contact"
                        control={control}
                        render={({ field }) => (
                          <Form.Select {...field} className="fs-10" size="sm">
                            <option value="">Select Contact Type</option>
                            <option value="disposable">Disposable</option>
                            <option value="permanent">Permanent</option>
                          </Form.Select>
                        )}
                      />
                    </Form.Group>
                  )}
                </Col>
                {isLens && (
                  <Col xs={12}>
                    <Form.Group controlId="lensType">
                      <label className="form-label">
                        Lenses Specifications
                      </label>
                      <Controller
                        name="lens_specifications"
                        control={control}
                        rules={{ required: false }}
                        render={({ field }) => (
                          <MultiSelect
                            {...field}
                            isMulti
                            menuPlacement="auto"
                            closeMenuOnSelect={false}
                            options={[
                              "Multicoated",
                              "Multifocal",
                              "Light",
                              "Aspheric",
                              "Bifocal",
                              "Medium",
                              "Lenticular",
                              "AntiScratch",
                              "Dark",
                              "SafetyThickness",
                              "AntiReflectingCoating",
                              "PhotoSensitive",
                              "HighIndex",
                              "Colored",
                              "SingleVision",
                              "Varilux",
                            ].map((opt) => ({
                              value: opt.toLowerCase(),
                              label: opt,
                            }))}
                            isClearable
                            placeholder="Select Lens Specifications..."
                            value={field.value || []}
                            onChange={(selected) => {
                              setValue("lens_specifications", selected || []);
                              // dispatch(handleFillStepData({ check: "lens_specifications", value: selected }))
                            }}
                          />
                        )}
                      />
                    </Form.Group>
                  </Col>
                )}
              </>
            )}
          </Row>
          {(isLens || isContact) && (
            <Row className="g-2">
              <Col xs={12}>{renderEyeTable("Right", true)}</Col>
              <Col xs={12}>{renderEyeTable("Left", true)}</Col>
            </Row>
          )}
          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="secondary"
              onClick={() => {
                if (validationErrors?.length === 0) {
                  setShowModal(false);
                } else {
                  toast.warning("Please fill all required fields correctly.");
                }
              }}
              className="me-2 fs-10"
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" className="fs-10">
              Save
            </Button>
          </div>
        </Form>
      </Modal.Body>
      <ShowError validationErrors={validationErrors} errors={errors} />
    </Modal>
  );
};

export default OpticalDetailsEditModal;
